// ===========================================
// Google Drive Tab
// Admin management of Google Drive integration
// ===========================================

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Switch,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Button,
  Chip,
} from '@mui/material';
import {
  Folder as DriveIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  InsertDriveFile as FileIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { extractSchoolSlugFromPath } from '../../../utils/routing';
import {
  useIntegrationSettings,
  useUpdateIntegrationSettings,
  useIntegrationOverview,
  integrationsKeys,
} from '../../../hooks/useIntegrations';
import { useRevokeGoogleDriveAccess } from '../../../hooks/useGoogleDrive';
import ConfirmDialog from '../../common/ConfirmDialog';
import { usePermissions } from '../../../hooks/usePermissions';

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function GoogleDriveTab() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Connect/Disconnect need integrations.write; the two switches save via system_settings.write (roles plan 5.6).
  const canManageDrive = can('integrations', 'write');
  const canSaveSettings = can('system_settings', 'write');
  const queryClient = useQueryClient();
  const { data: settings, isLoading: settingsLoading } = useIntegrationSettings();
  const { data: overview, isLoading: overviewLoading } = useIntegrationOverview();
  const updateSettings = useUpdateIntegrationSettings();
  const revokeMutation = useRevokeGoogleDriveAccess();

  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  const driveSettings = settings?.googleDrive;
  const driveOverview = overview?.googleDrive;

  const handleDisconnect = async () => {
    await revokeMutation.mutateAsync();
    // useRevokeGoogleDriveAccess invalidates googleDriveKeys.all; this tab reads
    // connection state from the integration overview query, so refresh that too
    // (otherwise the Connected chip / buttons stay stale until a manual reload).
    queryClient.invalidateQueries({ queryKey: integrationsKeys.overview() });
    setShowDisconnectDialog(false);
  };

  const handleToggleEnabled = async () => {
    if (!driveSettings) return;
    await updateSettings.mutateAsync({
      googleDrive: { enabled: !driveSettings.enabled },
    });
  };

  const handleToggleAutoSync = async () => {
    if (!driveSettings) return;
    await updateSettings.mutateAsync({
      googleDrive: { autoSync: !driveSettings.autoSync },
    });
  };

  const handleManageConnection = () => {
    const schoolSlug = extractSchoolSlugFromPath(window.location.pathname);
    navigate(schoolSlug ? `/${schoolSlug}/admin/google-drive` : '/admin/google-drive');
  };

  if (settingsLoading || overviewLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!driveSettings) {
    return <Alert severity="error">Failed to load Google Drive settings.</Alert>;
  }

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Google Drive integration allows your school to share files with students and parents through
        a linked Google Drive folder. Files are synced automatically.
      </Typography>

      {/* Master Enable/Disable */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          avatar={<DriveIcon color={driveSettings.enabled ? 'primary' : 'disabled'} />}
          title="Google Drive Integration"
          action={
            <Switch
              checked={driveSettings.enabled}
              onChange={handleToggleEnabled}
              disabled={updateSettings.isPending || !canSaveSettings}
            />
          }
        />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Enable or disable Google Drive integration for your school.
          </Typography>
        </CardContent>
      </Card>

      {/* Connection Status & Settings */}
      {driveSettings.enabled && (
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Connection Status"
            action={
              driveOverview?.isConnected ? (
                <Chip label="Connected" color="success" size="small" />
              ) : (
                <Chip label="Not Connected" color="warning" size="small" />
              )
            }
          />
          <CardContent>
            <Stack spacing={3}>
              {driveOverview?.isConnected ? (
                <>
                  {/* Stats */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Sync Summary
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FolderIcon color="primary" />
                        <Typography variant="body2">
                          {driveOverview.linkedFolders} linked folders
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <FileIcon color="primary" />
                        <Typography variant="body2">{driveOverview.totalFiles} files</Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  <Divider />

                  {/* Auto Sync Toggle */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2">Automatic Sync</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Automatically sync files when changes are detected
                      </Typography>
                    </Box>
                    <Switch
                      checked={driveSettings.autoSync}
                      onChange={handleToggleAutoSync}
                      disabled={updateSettings.isPending || !canSaveSettings}
                    />
                  </Box>

                  <Divider />

                  {/* Last Sync */}
                  {driveOverview.lastSyncAt && (
                    <Box>
                      <Typography variant="subtitle2">Last Sync</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(driveOverview.lastSyncAt).toLocaleString()}
                      </Typography>
                    </Box>
                  )}

                  <Divider />

                  {/* Manage / Disconnect Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" onClick={handleManageConnection}>
                      Manage Folders & Files
                    </Button>
                    {canManageDrive && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<LinkOffIcon />}
                        onClick={() => setShowDisconnectDialog(true)}
                        disabled={revokeMutation.isPending}
                      >
                        Disconnect
                      </Button>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    Connect your school&apos;s Google Drive to share files with students and parents.
                  </Typography>
                  {canManageDrive ? (
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleManageConnection}
                    >
                      Connect Google Drive
                    </Button>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      You don&apos;t have permission to connect Google Drive.
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card>
        <CardHeader title="How it works" />
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              <strong>1. Connect your Google account:</strong> Link your school&apos;s Google Drive
              to enable file sharing.
            </Typography>
            <Typography variant="body2">
              <strong>2. Select folders to share:</strong> Choose which folders should be accessible
              to teachers, students, and parents.
            </Typography>
            <Typography variant="body2">
              <strong>3. Automatic sync:</strong> Files are automatically synced when changes are
              made in Google Drive.
            </Typography>
            <Typography variant="body2">
              <strong>4. Permission-based access:</strong> Users only see files in folders they have
              access to based on their role and enrollments.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <ConfirmDialog
        open={showDisconnectDialog}
        title="Disconnect Google Drive"
        message="Are you sure you want to disconnect Google Drive? Synced files will remain in the portal, but automatic syncing will stop. You can reconnect at any time."
        confirmLabel="Disconnect"
        confirmColor="error"
        loading={revokeMutation.isPending}
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectDialog(false)}
      />
    </Box>
  );
}
